import React, { useEffect, useRef, useState } from 'react';
import CluePreview from './CluePreview';
import ClueField from './ClueField';
import WordPeek from './WordPeek';
import { QUICK_MODIFIERS, splitIconClue, withModifier, withQuotes } from './clueUtils';

const PEEK_DELAY = 300;

// Quick modifiers for an icon clue: ×2 / ×3 toggle, + and − start a letter
// modifier and put the caret at the end so the letter can be typed straight away,
// ” toggles quotes at the icon's top corners.
export const ModifierBar = ({ clue, onChange, onFocusEnd }) => {
  const parts = splitIconClue(clue);
  if (!parts) return null;
  const current = parts.modifier;

  const toggle = (mod) => onChange(withModifier(clue, current === mod ? '' : mod));
  const startLetter = (sign) => {
    onChange(withModifier(clue, current.startsWith(sign) ? '' : sign));
    if (!current.startsWith(sign)) onFocusEnd();
  };

  return (
    <div className="modifier-bar" role="group" aria-label="Icon modifier">
      {QUICK_MODIFIERS.map(m => (
        <button key={m.value} type="button" className={`mod-chip ${current === m.value ? 'on' : ''}`}
          aria-pressed={current === m.value} onClick={() => toggle(m.value)}>{m.label}</button>
      ))}
      <button type="button" className={`mod-chip ${current.startsWith('+') ? 'on' : ''}`}
        aria-pressed={current.startsWith('+')} onClick={() => startLetter('+')}
        title="Add letters">+</button>
      <button type="button" className={`mod-chip ${current.startsWith('-') ? 'on' : ''}`}
        aria-pressed={current.startsWith('-')} onClick={() => startLetter('-')}
        title="Remove letters">−</button>
      <button type="button" className={`mod-chip ${parts.quoted ? 'on' : ''}`}
        aria-pressed={parts.quoted} onClick={() => onChange(withQuotes(clue, !parts.quoted))}
        title="Quotes">”</button>
      {current && (
        <button type="button" className="mod-chip mod-clear" onClick={() => onChange(withModifier(clue, ''))}
          title="Remove modifier" aria-label="Remove modifier">
          <span className="material-symbols-outlined" aria-hidden="true">close</span>
        </button>
      )}
    </div>
  );
};

export const ToolButton = ({ icon, label, onClick, children }) => (
  <button type="button" className="clue-tool" onClick={onClick} title={label} aria-label={label}>
    {icon ? <span className="material-symbols-outlined" aria-hidden="true">{icon}</span> : children}
  </button>
);

const CluesPanel = ({
  words,
  clues,
  activeWordId,
  pastIndex,
  onUpdateClue,
  onInsertSoftHyphen,
  onAutoHyphenate,
  onOpenEmoji,
  deskOpen,
  onRequestSuggest,
  onFocusWord,
  onBlurWord,
  onHoverWord,
  textareaRefs
}) => {
  const [peek, setPeek] = useState(null); // { answer, rect }
  const peekTimer = useRef(null);

  useEffect(() => () => clearTimeout(peekTimer.current), []);

  const showPeek = (e, word) => {
    onHoverWord(word.id);
    clearTimeout(peekTimer.current);
    const rect = e.currentTarget.getBoundingClientRect();
    peekTimer.current = setTimeout(() => setPeek({ answer: word.answer, rect }), PEEK_DELAY);
  };
  const hidePeek = () => {
    onHoverWord(null);
    clearTimeout(peekTimer.current);
    setPeek(null);
  };

  const focusEnd = (wordId) => setTimeout(() => {
    const input = textareaRefs.current[wordId];
    if (!input) return;
    input.focus();
    input.setSelectionRange(input.value.length, input.value.length);
  }, 0);

  const acrossWords = words.filter(w => w.direction === 'across');
  const downWords = words.filter(w => w.direction === 'down');
  const ordered = [...acrossWords, ...downWords];

  // Enter jumps to the next clue (Shift+Enter to the previous one).
  const handleKeyDown = (e, word) => {
    if (e.key !== 'Enter') return;
    e.preventDefault();
    const i = ordered.findIndex(w => w.id === word.id);
    const next = ordered[(i + (e.shiftKey ? -1 : 1) + ordered.length) % ordered.length];
    if (next) textareaRefs.current[next.id]?.focus();
  };

  const renderWordItem = (word) => {
    const clue = clues[word.id] || '';
    const active = activeWordId === word.id;
    return (
      <div key={word.id} className={`word-item ${active ? 'active' : ''} ${clue.trim() ? '' : 'missing'}`}>
        <div className="word-header">
          <span
            className="word-label"
            onMouseEnter={e => showPeek(e, word)}
            onMouseLeave={hidePeek}
          >
            {word.answer}
          </span>
          <div className="word-tools">
            <ToolButton icon="add_reaction" label="Insert emoji at cursor" onClick={() => onOpenEmoji(word.id, textareaRefs.current[word.id])} />
            <ToolButton icon="remove" label="Insert soft hyphen at cursor" onClick={() => onInsertSoftHyphen(word.id, textareaRefs.current[word.id])} />
            <ToolButton icon="auto_fix_high" label="Auto-place soft hyphens" onClick={() => onAutoHyphenate(word.id)} />
          </div>
        </div>
        <div className="clue-input-container">
          <CluePreview clue={clue} />
          <ClueField
            ref={el => textareaRefs.current[word.id] = el}
            suggestOpen={deskOpen && active}
            onRequestSuggest={() => onRequestSuggest(word)}
            value={clue}
            onChange={e => onUpdateClue(word.id, e.target.value)}
            onFocus={() => onFocusWord(word)}
            onBlur={onBlurWord}
            onKeyDown={e => handleKeyDown(e, word)}
            className="clue-input"
            aria-label={`Clue for ${word.answer}`}
          />
        </div>
        {active && (
          <ModifierBar
            clue={clue}
            onChange={value => onUpdateClue(word.id, value)}
            onFocusEnd={() => focusEnd(word.id)}
          />
        )}
      </div>
    );
  };

  return (
    <div className="clues-panel">
      <div className="clues-panel-head">
        <span className="clues-links">
          <a href="https://fonts.google.com/icons" target="_blank" rel="noopener noreferrer" className="icon-link" title="Material Symbols">MS</a>
          <a href="https://fonts.google.com/noto/specimen/Noto+Emoji/glyphs" target="_blank" rel="noopener noreferrer" className="icon-link" title="Noto Emoji">NE</a>
        </span>
      </div>
      {words.length === 0 ? (
        <p className="clues-empty">No words yet.</p>
      ) : (
        <div className="words-columns">
          <div className="words-column">
            <div className="words-column-header">Across</div>
            <div className="words-list">
              {acrossWords.map(renderWordItem)}
            </div>
          </div>
          <div className="words-column">
            <div className="words-column-header">Down</div>
            <div className="words-list">
              {downWords.map(renderWordItem)}
            </div>
          </div>
        </div>
      )}

      {peek && <WordPeek answer={peek.answer} rect={peek.rect} pastClues={pastIndex.get(peek.answer) || []} />}

    </div>
  );
};

export default CluesPanel;
